import type { ScreenContext } from "../../shell/types";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { openModal } from "../../ui/modal";
import { chip, ib, table } from "../../ui/record";
import { fillPager, listPer, rememberPer, searchBox } from "../../ui/list";
import type { PageInfo } from "../../../server/rules/list-page";
import { area, field, refuse, select } from "../clients/forms";

// Checking and validating (brief 1D-4, mockup v48 valPage, vrec and cov247):
// - To validate (Home Care › Schedule): the week's completed visits with the counts (n clean, n
//   need a look, n sent back) and Validate n clean; the groups Needs a look, Clean, Sent back to the
//   caregiver and Validated; the table Visit, Caregiver, In · out, Tasks, Exceptions, with the
//   Validate and Open the visit record icons; filters by client, caregiver and exception.
// - The visit record sheet: the exception chips and its status; the icons Validate, Send back,
//   Correct a time and Release a hold; the Correct a time form (Which, Real time, Reason, Note) and
//   the Send back form; Times; Documentation check; Tasks from the care plan; Note and sign-off.
// - 24/7: each 24/7 patient's week by the client's shift pattern, who worked each slot, Note in or
//   Note missing, RN stop-ins, Not covered, and "x of y slots covered".
// Validated visits are what billing and payroll use. Nothing here stops care.

interface Exc {
  key: string;
  kind: string;
  words: string;
}
interface Row {
  id: string;
  group: string;
  patient: string;
  caregiver: string;
  when: string;
  inOut: string;
  tasks: string;
  exceptions: Exc[];
  /** Times-a-week tasks behind this week (brief 1E-10). */
  behind: string[];
}
interface Queue extends PageInfo {
  week: { start: string; end: string; label: string };
  canValidate: boolean;
  autoValidate: boolean;
  counts: { look: number; clean: number; sent: number; done: number };
  groups: { key: string; label: string; visits: Row[] }[];
  filters: {
    clients: { value: string; label: string }[];
    caregivers: { value: string; label: string }[];
    kinds: { value: string; label: string }[];
  };
}
interface Rec {
  visit: { id: string; title: string; sub: string; caregiverFirst: string; status: string };
  group: string;
  groupChip: string;
  exceptions: Exc[];
  corrections: {
    kind: string;
    was: string;
    now: string;
    reason: string;
    note: string;
    by: string;
    at: string;
    late: boolean;
    daysAfter: number;
  }[];
  holds: { id: string; words: string }[];
  times: { scheduled: string; checkedIn: string; checkedOut: string; mileage: string };
  /** Brief 1E-13: billed and paid hours, only for people with Sees financials. */
  hours: { billed: string; changed: string | null; paid: string } | null;
  docCheck: { key: string; label: string; ok: boolean }[];
  tasks: { task: string; result: string; tone: string; note: string | null }[];
  alsoDone: string | null;
  behind: string[];
  note: string | null;
  noteSubmitted: boolean;
  /** Brief 1E-18: a note changed after check-out, Running late?'s text, Told the patient. */
  lateEntry: { words: string; was: string } | null;
  lateText: string | null;
  told: string | null;
  printed: { prints: string; service: string; signed: string; url: string };
  signoff: string;
  sentBack: {
    note: string;
    at: string;
    by: string;
    open: boolean;
    resubmitted: string | null;
  } | null;
  validated: { how: string; by: string; at: string; reason: string | null } | null;
  canValidate: boolean;
  canRelease: boolean;
  correctionReasons: string[];
  releaseReasons: string[];
  lateCorrectionDays: number;
  missedPunchPattern: number;
}
interface Coverage {
  week: { start: string; end: string; label: string };
  episodes: {
    episodeId: string;
    patient: string;
    patientId: string;
    service: string;
    coveredWords: string;
    days: { day: string; label: string }[];
    rows: {
      name: string;
      hours: string;
      cells: {
        day: string;
        inside: boolean;
        covered: boolean;
        ended: boolean;
        workers: {
          visitId: string;
          name: string;
          stopIn: boolean;
          stopInWords: string | null;
          noteIn: boolean;
          billed: boolean;
          done: boolean;
        }[];
      }[];
    }[];
  }[];
}

const exChips = (ex: readonly Exc[]) =>
  el(
    "span",
    { class: "vflags", "aria-label": "Exceptions" },
    ...(ex.length ? ex.map((e) => chip(e.words, "warn")) : [chip("Clean", "ok")]),
  );

// ---- To validate --------------------------------------------------------------------------------------

/** To validate (mockup valPage), drawn into `slot` for the week that starts on `week`. */
export async function validateView(
  slot: HTMLElement,
  ctx: ScreenContext,
  week: string | null,
  changed: () => void,
) {
  const f = { client: "", caregiver: "", kind: "" };
  let seq = 0;
  // Brief 1E-32: search and pages, asked of the server.
  const listId = "homecare/validate";
  let search = "";
  let pageNo = 1;
  let per = listPer(listId, ctx.me.user.id);
  const searchRow = searchBox("Search patients or caregivers", (text) => {
    search = text;
    pageNo = 1;
    void load();
  });
  const pager = el("nav", { class: "pager", "aria-label": "Pages of visits" });
  async function load() {
    const mine = ++seq;
    const q = new URLSearchParams();
    if (week) q.set("week", week);
    for (const [k, v] of Object.entries(f)) if (v) q.set(k, v);
    if (search.trim()) q.set("q", search.trim());
    q.set("page", String(pageNo));
    q.set("per", String(per));
    const res = await api<Queue>("GET", `/api/validation?${q.toString()}`);
    if (mine !== seq) return;
    if (!res.ok) {
      slot.replaceChildren(
        el("p", { class: "note", role: "alert" }, "The visits to validate couldn't be loaded."),
      );
      return;
    }
    draw(res.body);
  }
  const again = () => {
    changed();
    void load();
  };

  function draw(a: Queue) {
    pageNo = a.page;
    fillPager(pager, a, per, {
      per: async (n) => {
        per = n;
        rememberPer(listId, ctx.me.user.id, n);
        pageNo = 1;
        await load();
      },
      page: async (n) => {
        pageNo = n;
        await load();
      },
    });
    const validateClean = el(
      "button",
      { type: "button", class: "btn sm pri", disabled: !a.counts.clean || !a.canValidate },
      icon("check"),
      ` Validate ${a.counts.clean} clean`,
    );
    validateClean.addEventListener("click", async () => {
      validateClean.disabled = true;
      const r = await api<{ validated: number }>("POST", "/api/validation/validate-clean", {
        week: a.week.start,
      });
      if (!r.ok) {
        validateClean.disabled = false;
        return toast("That wasn't validated", r.body.error);
      }
      toast(
        `Validated ${r.body.validated} clean visit${r.body.validated === 1 ? "" : "s"}`,
        "Billing and payroll can use them",
      );
      again();
    });
    // Filters by client, caregiver and exception: small selects, one row.
    const pick = (label: string, key: keyof typeof f, opts: { value: string; label: string }[]) => {
      const s = el(
        "select",
        { "aria-label": label, class: "sm", style: "width:auto;max-width:220px" },
        el("option", { value: "" }, `Every ${label.toLowerCase()}`),
        ...opts.map((o) => el("option", { value: o.value, selected: o.value === f[key] }, o.label)),
      );
      s.addEventListener("change", () => {
        f[key] = s.value;
        pageNo = 1;
        void load();
      });
      return s;
    };
    const filters = el(
      "div",
      {
        style: "display:flex;gap:6px;flex-wrap:wrap;align-items:center;margin:8px 0 0",
        role: "group",
        "aria-label": "Filters",
      },
      icon("filter"),
      pick("Client", "client", a.filters.clients),
      pick("Caregiver", "caregiver", a.filters.caregivers),
      pick("Exception", "kind", a.filters.kinds),
    );
    const groups = a.groups
      .filter((g) => g.visits.length)
      .flatMap((g) => [
        el("h3", { style: "margin:12px 0 4px" }, `${g.label} · ${g.visits.length}`),
        table(
          [
            {
              h: "Visit",
              f: (v: Row) =>
                el("span", {}, el("b", {}, v.patient), el("div", { class: "small muted" }, v.when)),
            },
            { h: "Caregiver", f: (v) => v.caregiver },
            { h: "In · out", f: (v) => v.inOut },
            { h: "Tasks", f: (v) => v.tasks },
            {
              h: "Exceptions",
              f: (v) =>
                el(
                  "span",
                  {},
                  exChips(v.exceptions),
                  ...v.behind.map((b) => el("div", { class: "small" }, chip(b, "warn"))),
                ),
            },
            {
              h: "",
              f: (v) =>
                el(
                  "span",
                  { class: "vacts" },
                  ...(g.key !== "done" && a.canValidate
                    ? [
                        ib("check", `Validate ${v.patient} · ${v.when}`, () =>
                          v.exceptions.length
                            ? void openVisitRecord(ctx, v.id, again, true)
                            : void validateOne(v.id, null, again),
                        ),
                      ]
                    : []),
                  ib("eye", `Open the visit record: ${v.patient} · ${v.when}`, () =>
                    openVisitRecord(ctx, v.id, again),
                  ),
                ),
            },
          ],
          g.visits,
          undefined,
          g.label,
        ),
      ]);
    // The search box is kept across redraws, with its typing and focus.
    const typing = searchRow.contains(document.activeElement);
    queueMicrotask(() => typing && searchRow.querySelector<HTMLElement>("input")?.focus());
    slot.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "To validate" },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("checklist"), ` To validate · week of ${a.week.label}`),
            el(
              "span",
              { class: "small muted" },
              `Validated visits are what billing and payroll use. ${a.autoValidate ? "Clean visits validate themselves (a setting)" : "Clean visits wait for a person (automatic validation is off)"}; the rest wait here.`,
            ),
          ),
          el(
            "span",
            { class: "actions" },
            chip(`${a.counts.clean} clean`, "ok"),
            chip(`${a.counts.look} need a look`, "warn"),
            chip(`${a.counts.sent} sent back`, "info"),
            ...(a.canValidate ? [validateClean] : []),
          ),
        ),
        filters,
        el("div", { style: "margin:8px 0 0" }, searchRow),
        ...(groups.length
          ? groups
          : [
              el(
                "p",
                { class: "muted", style: "margin:12px 0 0" },
                search.trim() ? "Nothing matches this search." : "No completed visits this week.",
              ),
            ]),
        pager,
      ),
    );
  }
  await load();
}

async function validateOne(id: string, reason: string | null, done: () => void) {
  const r = await api("POST", `/api/visits/${id}/validate`, reason ? { reason } : {});
  if (!r.ok) {
    toast("That wasn't validated", r.body.error);
    return false;
  }
  toast("Validated", "Billing and payroll can use it");
  done();
  return true;
}

// ---- The visit record ----------------------------------------------------------------------------------

/**
 * The visit record sheet (mockup vrec): `validating` opens it with the reason asked for (a visit
 * with exceptions validated from the table).
 */
export async function openVisitRecord(
  ctx: ScreenContext,
  id: string,
  changed: () => void,
  validating = false,
) {
  const res = await api<Rec>("GET", `/api/visits/${id}/record`);
  if (!res.ok) return toast("That visit record couldn't be opened", res.body.error);
  const r = res.body;
  const formSlot = el("div", { "aria-live": "polite" });
  const after = (next: Rec, words: string, sub?: string) => {
    toast(words, sub);
    m.close();
    changed();
    void openVisitRecord(ctx, next.visit.id, changed);
  };
  const post = async (path: string, data: unknown) => {
    const x = await api<Rec>("POST", `/api/visits/${r.visit.id}/${path}`, data);
    if (!x.ok) {
      refuse(m.note, x.body.error);
      return null;
    }
    return x.body;
  };

  // Validate: straight away when clean; with exceptions, a short reason first.
  function validateForm() {
    if (!r.exceptions.length) {
      void (async () => {
        const next = await post("validate", {});
        if (next) after(next, "Validated", "Billing and payroll can use it");
      })();
      return;
    }
    const why = field("Why validate it with exceptions (kept with the visit)", "", {
      maxlength: "300",
    });
    const go = el("button", { type: "button", class: "btn pri" }, icon("check"), " Validate");
    go.addEventListener("click", async () => {
      go.disabled = true;
      const next = await post("validate", { reason: why.input.value });
      go.disabled = false;
      if (next) after(next, "Validated", "Billing and payroll can use it");
    });
    formSlot.replaceChildren(
      el(
        "form",
        { class: "card form", style: "margin:8px 0;max-width:none", "aria-label": "Validate" },
        el("h2", {}, "Validate"),
        el(
          "p",
          { class: "small muted" },
          "This visit still has exceptions. Say briefly why it's validated.",
        ),
        why.el,
        el("div", { class: "actions" }, go),
      ),
    );
    why.input.focus();
  }

  function correctForm() {
    const which = select(
      "Which",
      [
        ["out", "Check-out"],
        ["in", "Check-in"],
      ],
      r.times.checkedOut === "–" || r.times.checkedIn !== "–" ? "out" : "in",
    );
    const time = field("Real time", "", { type: "time" });
    const reason = select(
      "Reason",
      r.correctionReasons.map((x) => [x, x] as const),
      r.correctionReasons[0] ?? "",
    );
    const note = field("Note (required)", "", { maxlength: "500" });
    const go = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("check"),
      " Save correction",
    );
    go.addEventListener("click", async () => {
      go.disabled = true;
      const next = await post("correct", {
        kind: which.input.value,
        time: time.input.value,
        reason: reason.input.value,
        note: note.input.value,
      });
      go.disabled = false;
      if (next) after(next, "Correction saved");
    });
    formSlot.replaceChildren(
      el(
        "form",
        {
          class: "card form",
          style: "margin:8px 0;max-width:none",
          "aria-label": "Correct a time",
        },
        el("h2", {}, "Correct a time"),
        el("div", { class: "grid3", style: "gap:8px" }, which.el, time.el, reason.el),
        note.el,
        el(
          "p",
          { class: "small muted" },
          `A correction more than ${r.lateCorrectionDays} days later is flagged.`,
        ),
        el("div", { class: "actions" }, go),
      ),
    );
    time.input.focus();
  }

  function sendForm() {
    const what = area("What to fix", "");
    const go = el(
      "button",
      { type: "button", class: "btn pri" },
      icon("paper-airplane"),
      " Send to the field app",
    );
    go.addEventListener("click", async () => {
      go.disabled = true;
      const next = await post("send-back", { note: what.input.value });
      go.disabled = false;
      if (next) after(next, "Sent back", `It shows in ${r.visit.caregiverFirst}'s field app`);
    });
    formSlot.replaceChildren(
      el(
        "form",
        {
          class: "card form",
          style: "margin:8px 0;max-width:none",
          "aria-label": `Send back to ${r.visit.caregiverFirst}`,
        },
        el("h2", {}, `Send back to ${r.visit.caregiverFirst}`),
        what.el,
        el("div", { class: "actions" }, go),
      ),
    );
    what.input.focus();
  }

  function releaseForm() {
    const reason = select(
      "Why it's released",
      r.releaseReasons.map((x) => [x, x] as const),
      r.releaseReasons[0] ?? "",
    );
    const note = field("Note", "", { maxlength: "1000" });
    const go = el("button", { type: "button", class: "btn pri" }, icon("unlock"), " Release");
    go.addEventListener("click", async () => {
      go.disabled = true;
      const next = await post("release", { reason: reason.input.value, note: note.input.value });
      go.disabled = false;
      if (next) after(next, "Hold released");
    });
    formSlot.replaceChildren(
      el(
        "form",
        {
          class: "card form",
          style: "margin:8px 0;max-width:none",
          "aria-label": "Release a hold",
        },
        el("h2", {}, "Release a hold"),
        el("p", { class: "small" }, r.holds.map((h) => h.words).join(" · ")),
        el("div", { class: "grid2", style: "gap:8px" }, reason.el, note.el),
        el(
          "p",
          { class: "small muted" },
          "Releasing lets billing and payroll use it. It also goes on the fix list.",
        ),
        el("div", { class: "actions" }, go),
      ),
    );
  }

  const acts: HTMLElement[] = [];
  if (r.canValidate) {
    acts.push(
      ib("check", "Validate (billing and payroll can use it)", validateForm),
      ib("comment", "Send back to the caregiver with what to fix", sendForm),
      ib("clock", "Correct a time", correctForm),
    );
  }
  if (r.holds.length)
    acts.push(
      ib(
        "unlock",
        r.canRelease ? "Release a hold (needs the permission)" : "Release a hold (needs an admin)",
        () => (r.canRelease ? releaseForm() : toast("Releasing a hold needs an admin.")),
      ),
    );

  const kv = (rows: [string, string][]) =>
    el("dl", { class: "kv" }, ...rows.flatMap(([k, v]) => [el("dt", {}, k), el("dd", {}, v)]));
  const statusChips = el(
    "div",
    { style: "display:flex;gap:6px;flex-wrap:wrap;margin:8px 0", "aria-label": "Status" },
    ...(r.exceptions.length
      ? r.exceptions.map((e) => chip(e.words, "warn"))
      : [chip("Clean", "ok")]),
    chip(r.groupChip, r.group === "look" ? "warn" : r.group === "done" ? "ok" : "info"),
    ...r.corrections.map((c) =>
      chip(
        `Time corrected · ${c.reason.toLowerCase()}${c.late ? ` (${c.daysAfter} days later)` : ""}`,
        "info",
      ),
    ),
    ...r.holds.map((h) => chip(`Hold: ${h.words}`, "bad")),
  );
  const sentNote = r.sentBack
    ? el(
        "p",
        { class: "note", role: "note" },
        el("b", {}, r.sentBack.open ? "Sent back: " : "Was sent back: "),
        r.sentBack.note,
        el(
          "span",
          { class: "small muted" },
          ` · ${r.sentBack.by}, ${r.sentBack.at}${r.sentBack.resubmitted ? ` · sent again ${r.sentBack.resubmitted}` : ""}`,
        ),
      )
    : null;
  const validatedNote = r.validated
    ? el(
        "p",
        { class: "note", role: "note" },
        el("b", {}, "Validated: "),
        `${r.validated.by}, ${r.validated.at}${r.validated.reason ? ` · ${r.validated.reason}` : ""}`,
      )
    : null;
  const section = (label: string, ...kids: (Node | string)[]) =>
    el("section", { class: "card", "aria-label": label }, el("h2", {}, label), ...kids);
  const m = openModal({
    title: r.visit.title,
    sub: r.visit.sub,
    wide: true,
    body: [
      el(
        "div",
        { style: "display:flex;gap:6px;flex-wrap:wrap;justify-content:flex-end" },
        el("span", { class: "vacts" }, ...acts),
      ),
      statusChips,
      ...(validatedNote ? [validatedNote] : []),
      ...(sentNote ? [sentNote] : []),
      formSlot,
      el(
        "div",
        { class: "grid2" },
        section(
          "Times",
          kv([
            ["Scheduled", r.times.scheduled],
            ["Checked in", r.times.checkedIn],
            ["Checked out", r.times.checkedOut],
            ["Mileage", r.times.mileage],
            // Brief 1E-13: billed and paid hours side by side (Sees financials).
            ...(r.hours
              ? ([
                  ["Billed", `${r.hours.billed}${r.hours.changed ? ` · ${r.hours.changed}` : ""}`],
                  ["Paid", r.hours.paid],
                ] as [string, string][])
              : []),
          ]),
          ...r.corrections.map((c) =>
            el(
              "p",
              { class: "small muted", style: "margin:6px 0 0" },
              `${c.kind} ${c.was} → ${c.now} · ${c.reason}: ${c.note} · ${c.by}, ${c.at}`,
            ),
          ),
        ),
        section(
          "Documentation check",
          ...r.docCheck.map((c) =>
            el(
              "div",
              { class: "prow" },
              el("span", {}, icon(c.ok ? "check-circle" : "x-circle"), ` ${c.label}`),
              c.ok ? chip("OK", "ok") : chip("Missing", "bad"),
            ),
          ),
          el("p", { class: "small muted" }, "Which checks apply is set per client type."),
        ),
      ),
      el(
        "div",
        { class: "grid2" },
        section(
          "Tasks from the care plan",
          ...(r.tasks.length
            ? r.tasks.map((t) =>
                el(
                  "div",
                  { class: "prow" },
                  el("span", {}, t.task),
                  // Not done or Refused with the reason, as the caregiver gave it.
                  chip(t.note && t.tone === "warn" ? `${t.result} · ${t.note}` : t.result, t.tone),
                ),
              )
            : [el("p", { class: "muted" }, "No care tasks due this visit.")]),
          ...r.behind.map((b) =>
            el("p", { class: "small", style: "margin:6px 0 0" }, chip(b, "warn")),
          ),
          ...(r.alsoDone ? [kv([["Also done this visit", r.alsoDone]])] : []),
        ),
        section(
          "Note and sign-off",
          el("p", {}, r.note ?? el("span", { class: "muted" }, "No note yet.")),
          ...(r.lateEntry
            ? [
                el(
                  "p",
                  { class: "small", style: "margin:0 0 6px" },
                  chip(r.lateEntry.words, "warn"),
                ),
                el(
                  "p",
                  { class: "small muted", style: "margin:0 0 6px" },
                  `At check-out: ${r.lateEntry.was || "no note"}`,
                ),
              ]
            : []),
          kv([["Sign-off", r.signoff]]),
          ...[r.lateText, r.told]
            .filter((x): x is string => !!x)
            .map((x) => el("p", { class: "small muted", style: "margin:6px 0 0" }, x)),
        ),
      ),
      // On the printed note (mockup V15, brief 1E-18): the service ordered, signed with the
      // person's own name and title.
      el(
        "section",
        { class: "card", "aria-label": "On the printed note" },
        el(
          "div",
          { class: "cardhead" },
          el("h2", {}, icon("file"), " On the printed note"),
          ib("download", "Print the visit note", () => window.open(r.printed.url, "_blank")),
        ),
        kv([
          ["Prints", r.printed.prints],
          ["Billed as", `${r.printed.service}, the service ordered`],
          ["Signed", r.printed.signed],
        ]),
      ),
    ],
  });
  if (validating) validateForm();
}

// ---- 24/7 coverage --------------------------------------------------------------------------------------

/** The 24/7 view (mockup cov247), drawn into `slot` for the week that starts on `week`. */
export async function coverageView(
  slot: HTMLElement,
  ctx: ScreenContext,
  week: string | null,
  changed: () => void,
) {
  const res = await api<Coverage>("GET", `/api/coverage${week ? `?week=${week}` : ""}`);
  if (!res.ok) {
    slot.replaceChildren(
      el("p", { class: "note", role: "alert" }, "The 24/7 coverage couldn't be loaded."),
    );
    return;
  }
  const c = res.body;
  if (!c.episodes.length) {
    slot.replaceChildren(
      el(
        "section",
        { class: "card", "aria-label": "24/7 coverage" },
        el("h2", {}, icon("clock"), " 24/7 coverage"),
        el("p", { class: "muted" }, "No patient has continuous coverage this week."),
      ),
    );
    return;
  }
  slot.replaceChildren(
    ...c.episodes.map((e) => {
      const cells = e.rows.flatMap((row) => [
        el(
          "div",
          { class: "vkrow" },
          el("b", {}, row.name),
          el("span", { class: "small muted" }, row.hours),
        ),
        ...row.cells.map((cell) => {
          if (!cell.inside) return el("div", {}, el("span", { class: "small muted" }, "–"));
          if (!cell.workers.length)
            return el(
              "div",
              {},
              el("span", { class: "vkchip no", style: "cursor:default" }, "Not covered"),
            );
          return el(
            "div",
            {},
            ...cell.workers.map((w) => {
              const b = el(
                "button",
                {
                  type: "button",
                  class: `vkchip${w.noteIn || !cell.ended ? "" : " wait"}`,
                  "aria-label": `${w.name}${w.stopIn ? ` (${w.stopInWords})` : ""} · ${w.noteIn ? (w.stopIn ? "Visit note" : "Note in") : cell.ended ? "Note missing" : "Not done yet"}`,
                },
                `${w.name}${w.stopIn ? ` (${w.stopInWords})` : ""}`,
                el("br"),
                w.noteIn
                  ? el("span", {}, `${w.stopIn ? "Visit note" : "Note in"} `, icon("check-circle"))
                  : cell.ended
                    ? "Note missing"
                    : "Scheduled",
              );
              b.addEventListener("click", () =>
                w.done || cell.ended
                  ? void openVisitRecord(ctx, w.visitId, changed)
                  : toast("Not done yet", "The visit record opens once it's checked out"),
              );
              return b;
            }),
          );
        }),
      ]);
      return el(
        "section",
        { class: "card", "aria-label": `24/7 coverage · ${e.patient}` },
        el(
          "div",
          { class: "cardhead" },
          el(
            "div",
            {},
            el("h2", {}, icon("clock"), ` 24/7 coverage · ${e.patient}`),
            el("span", { class: "small muted" }, `Slots come from the client's shift pattern.`),
          ),
          chip(e.coveredWords, "info"),
        ),
        el(
          "div",
          { class: "vkwrap" },
          el(
            "div",
            {
              class: "vkgrid",
              style: `grid-template-columns:160px repeat(${e.days.length},minmax(118px,1fr))`,
              role: "table",
              "aria-label": `Slots · ${e.patient}`,
            },
            el("div", { class: "vkh" }, el("b", {}, "Slot")),
            ...e.days.map((d) => el("div", { class: "vkh" }, el("b", {}, d.label))),
            ...cells,
          ),
        ),
      );
    }),
  );
}
